import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, DollarSign, Eye, LockKeyhole, Target, Users } from 'lucide-react';
import { Avatar, Container, Glow } from './ui';
import { EASE, Stagger, StaggerItem } from './motion';

const FundCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 40, rotateX: 12 }}
    animate={{ opacity: 1, y: 0, rotateX: 0 }}
    transition={{ duration: 1, ease: EASE, delay: 0.5 }}
    style={{ transformPerspective: 1200 }}
    className="relative mx-auto w-full max-w-lg"
  >
    <Glow className="-inset-10 bg-gradient-to-tr from-brand-500/30 via-blue-600/20 to-violet-600/20" />

    <div className="surface relative !bg-ink-900/90 p-5 sm:p-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-300/60 to-transparent" />

      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-2xl font-semibold tracking-tight">Group Trip Fund</h3>
          <p className="mt-1 text-sm text-slate-400">Hawaii 2025</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-sm font-medium text-emerald-300">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:hidden" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          Active
        </span>
      </div>

      <div className="mt-8 space-y-3">
        <div className="surface-inset flex items-center justify-between p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-400/10 text-brand-300">
              <DollarSign className="h-5 w-5" />
            </span>
            <span className="text-slate-300">Total Pooled</span>
          </div>
          <span className="text-xl font-bold tabular-nums tracking-tight sm:text-2xl">$2,450.00</span>
        </div>

        <div className="surface-inset p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-400/10 text-brand-300">
                <Target className="h-5 w-5" />
              </span>
              <span className="whitespace-nowrap text-slate-300">Goal Progress</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold tabular-nums text-brand-300">70%</span>
              <span className="hidden text-sm text-slate-500 sm:inline">of $3,500</span>
            </div>
          </div>
          <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-brand-400 to-brand-300 shadow-[0_0_16px_rgba(0,211,255,0.6)]"
              initial={{ width: 0 }}
              animate={{ width: '70%' }}
              transition={{ duration: 1.4, ease: EASE, delay: 1.1 }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-slate-500">
            <span>$0</span>
            <span>$3,500</span>
          </div>
        </div>

        <div className="surface-inset flex items-center justify-between p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-400/10 text-brand-300">
              <Users className="h-5 w-5" />
            </span>
            <span className="text-slate-300">Contributors</span>
          </div>
          <div className="flex -space-x-2">
            <Avatar initials="JD" color="blue" size="sm" />
            <Avatar initials="KS" color="green" size="sm" />
            <Avatar initials="MR" color="amber" size="sm" />
            <Avatar initials="+5" color="purple" size="sm" />
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <button type="button" className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-brand-400 py-3 font-semibold text-ink-950 transition-colors duration-200 hover:bg-brand-300">
          <Eye className="h-4 w-4" />
          View Details
        </button>
        <button type="button" className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-white/10 py-3 font-semibold text-slate-200 transition-colors duration-200 hover:border-white/25 hover:bg-white/[0.05]">
          <DollarSign className="h-4 w-4" />
          Contribute
        </button>
      </div>

      <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-500">
        <LockKeyhole className="h-3.5 w-3.5" />
        Secure transactions with Groupmoney
      </p>
    </div>
  </motion.div>
);

const Hero = () => (
  <section id="top" className="relative overflow-hidden pb-24 pt-40 sm:pb-32 sm:pt-48">
    {/* Ambient backdrop */}
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute inset-0 bg-grid-fade bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
      <Glow className="left-1/2 top-[-12rem] h-[36rem] w-[60rem] -translate-x-1/2 bg-brand-500/20" />
      <Glow className="right-[-10rem] top-[30rem] h-[28rem] w-[28rem] bg-violet-600/10" />
    </div>

    <Container className="relative">
      <Stagger className="mx-auto max-w-5xl text-center" stagger={0.12}>
        <StaggerItem
          as="h1"
          className="text-[3.25rem] font-extrabold leading-[1.02] tracking-tightest pb-2 sm:text-7xl lg:text-[7.5rem]"
        >
          <span className="text-gradient pr-[0.06em]">Simplifying</span>{' '}
          <span className="text-gradient-brand pr-[0.06em]">Group Finances</span>
        </StaggerItem>
        <StaggerItem
          as="p"
          className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-slate-400 sm:text-2xl sm:leading-relaxed"
        >
          Pool money with others easily, transparently, and stress-free. Focus on what really matters—achieving your goals together.
        </StaggerItem>
        <StaggerItem className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
          <a href="#get-started" className="btn-primary group w-full sm:w-auto">
            Get Started For Free
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </a>
          <a href="#features" className="btn-secondary w-full sm:w-auto">
            Learn More
          </a>
        </StaggerItem>
      </Stagger>

      <div className="mt-20 sm:mt-24">
        <FundCard />
      </div>
    </Container>
  </section>
);

export default Hero;
